import { blue } from '@mui/material/colors'
import { isWithinInterval, parseISO, startOfDay, endOfDay } from 'date-fns'
import { get } from 'utils/lodash'
import { PAYMENT_METHODS, PAYMENT_METHODS_LABELS } from 'settings/constants/payment'
import { handleInvoiceSelectDeselect } from 'utils/payment'
import { formatCentsToDollars, formatDollarsToCents } from 'utils/price'
import { formatDateToBEFormatDateFns } from 'utils/date'
import T from 'T'

export const METHODS_COLOR_MAP = {
  [PAYMENT_METHODS.CHECK]: blue[100],
  [PAYMENT_METHODS.CASH]: blue[400],
  [PAYMENT_METHODS.CREDIT_CARD]: blue[500],
  [PAYMENT_METHODS.ACH]: blue[300],
  [PAYMENT_METHODS.ACCOUNT_CREDIT]: blue[200],
  [PAYMENT_METHODS.OTHER]: blue[700],
}

export const BATCH_STATUS = {
  DRAFT: 'DRAFT',
  STARTED: 'STARTED',
  POSTED: 'POSTED',
  POSTING: 'POSTING',
  COMPLETED: 'COMPLETED',
}

export const PAYMENT_BATCH_MANUAL = 'PAYMENT_BATCH_MANUAL'
export const PAYMENT_BATCH_CSV_IMPORT = 'PAYMENT_BATCH_CSV_IMPORT'

export const getChartData = payments => {
  return Object.values(PAYMENT_METHODS)
    .map(method => {
      const count = payments.filter(p => p.method === method && p.amount > 0).length
      if (count === 0) return null
      return {
        id: method,
        label: method,
        value: count,
        color: METHODS_COLOR_MAP[method] || blue[500], // Default color if not found
      }
    })
    .filter(Boolean)
}

export const getPaymentsByType = payments => {
  return Object.entries(PAYMENT_METHODS_LABELS).reduce((acc, [type, label]) => {
    const filtered = payments.filter(p => p.method === type)
    acc[type] = {
      label,
      rows: filtered,
      count: filtered.length,
      total: filtered.reduce((sum, p) => sum + (parseFloat(p.amount) || 0) * 100, 0),
    }
    return acc
  }, {})
}

export const CELL_STATUS = {
  SAVED: T.SAVED,
  SAVING: T.SAVING,
  ERROR: T.ERROR,
  IDLE: T.IDLE,
}

export const selectInvoices = (sortedUnpaidInvoices, invoices, amount = 0, newlySelectedInvoiceNumber = [], showToastMessages = true) => {
  const result = handleInvoiceSelectDeselect(
    invoices,
    0,
    formatDollarsToCents(amount),
    newlySelectedInvoiceNumber,
    sortedUnpaidInvoices,
    showToastMessages
  )

  const { selectedInvoices = [] } = result

  const totalCents = selectedInvoices.reduce((sum, invoice) => sum + invoice.usedAmount, 0)
  const credit = formatCentsToDollars(formatDollarsToCents(amount) - totalCents)

  const invoiceNums = selectedInvoices.map(inv => inv.invoiceNumber)

  const invoicesToSelect = sortedUnpaidInvoices.filter(inv => invoiceNums.includes(inv.invoiceNumber))

  return { invoicesToSelect, sortedUnpaidInvoices, credit, selectedInvoices }
}

export const parseBatchForPostPayload = (batch = {}, source = PAYMENT_BATCH_MANUAL) => {
  const date = new Date()
  const batchDate = batch.batchDate || formatDateToBEFormatDateFns(date)

  const requestedPayments =
    batch.payments?.map(payment => {
      const amountInDollars = parseFloat(payment.amount) || 0
      const amountCents = Math.round(amountInDollars * 100)

      return {
        accountId: payment.account?.accountId || '',
        customerName: payment.account?.customerName || payment.customerName || '',
        paymentMethod: payment.method || '',
        paymentDate: payment.transactionDate
          ? formatDateToBEFormatDateFns(new Date(payment.transactionDate))
          : formatDateToBEFormatDateFns(date),
        checkNum: payment.checkNumber || '',
        amount: amountInDollars, // amount in cents
        amountCents: amountCents, // duplicate field as shown in example
        invoicesToPay: payment.selectedInvoices?.map(invoice => invoice.invoiceNumber).filter(Boolean) || [],
        note: payment.note || '',
      }
    }) || []

  return {
    batchDate,
    id: batch.id,
    requestedPayments,
    source,
  }
}

export const parseBatchForDraftPayload = (batch = {}, source = PAYMENT_BATCH_MANUAL) => {
  const batchPayload = {
    id: batch.id,
    businessId: batch.businessId,
    batchName: batch.batchId,
    archived: false,
    status: batch.status,
  }

  const parsedBatch = {
    status: batch.status,
    source,
    payments: batch.payments?.map(p => {
      return {
        ...p,
        invoices: p.selectedInvoices.map(i => ({
          invoiceNumber: i.invoiceNumber,
          invoiceAmount: i.invoiceAmount,
          isLocked: i.isLocked,
        })),
        account: {
          accountId: p.account?.accountId || '',
          accountNumber: p.account?.accountNumber || '',
        },
        allUnpaidInvoices: p.allUnpaidInvoices.map(i => ({
          invoiceNumber: i.invoiceNumber,
          invoiceAmount: i.invoiceAmount,
          isLocked: i.isLocked,
        })),
        selectedInvoices: p.selectedInvoices.map(i => ({
          invoiceNumber: i.invoiceNumber,
          invoiceAmount: i.invoiceAmount,
          isLocked: i.isLocked,
        })),
      }
    }),
  }

  batchPayload.batch = JSON.stringify(parsedBatch)

  return batchPayload
}

export const parseBatchJSON = (batch = {}) => {
  if (!batch || !batch.batch) return {}
  try {
    const parsedBatch = JSON.parse(batch.batch)

    const result = {
      ...batch,
      payments:
        parsedBatch.payments?.map(payment => ({
          ...payment,
          invoices: payment.allUnpaidInvoices,
        })) || [],
    }
    delete result.batch
    return result
  } catch (error) {
    console.error(T.FAILED_TO_PARSE_BATCH_JSON, error)
    return {}
  }
}

export const validatePaymentBatch = (payments = []) => {
  const errors = []
  const today = new Date()
  today.setHours(23, 59, 59, 999) // Set to end of today for comparison
  const paymentErrors = {}

  if (!payments || payments.length === 0) {
    errors.push(T.BATCH_MUST_CONTAIN_AT_LEAST_ONE_PAYMENT)
    return { isValid: false, errors }
  }

  payments.forEach((payment, index) => {
    const rowErrors = []

    // Validate account
    if (!payment.account?.accountNumber) {
      rowErrors.push({
        message: T.ACCOUNT_IS_REQUIRED,
        field: 'accountNumber',
      })
    }

    if (!payment.account?.accountId) {
      rowErrors.push({
        message: T.ACCOUNT_NOT_FOUND_ERROR,
        field: 'accountNumber',
      })
    }

    // Validate amount
    const amount = parseFloat(payment.amount)
    if (!payment.amount || isNaN(amount) || amount <= 0) {
      rowErrors.push({
        message: T.AMOUNT_MUST_BE_GREATER_THAN_ZERO,
        field: 'amount',
      })
    }

    // Validate method
    if (!payment.method || !Object.values(PAYMENT_METHODS).includes(payment.method)) {
      rowErrors.push({
        message: T.PAYMENT_METHOD_IS_REQUIRED,
        field: 'method',
      })
    }

    // Validate check number if method is CHECK
    if (payment.method === PAYMENT_METHODS.CHECK && (!payment.checkNumber || payment.checkNumber.trim() === '')) {
      rowErrors.push({
        message: T.CHECK_NUMBER_REQUIRED_FOR_CHECK_PAYMENTS,
        field: 'checkNumber',
      })
    }

    // Validate transaction date
    if (!payment.transactionDate) {
      rowErrors.push({
        message: T.TRANSACTION_DATE_IS_REQUIRED,
        field: 'transactionDate',
      })
    } else {
      const transactionDate = new Date(payment.transactionDate)
      if (transactionDate > today) {
        rowErrors.push({
          message: T.TRANSACTION_DATE_CANNOT_BE_IN_FUTURE,
          field: 'transactionDate',
        })
      }
    }

    // Add row-specific errors to main errors array
    if (rowErrors.length > 0) {
      paymentErrors[index] = rowErrors
      errors.push(`Payment ${index + 1} has errors: ${rowErrors.map(e => e.message).join(', ')}`)
    }
  })

  return {
    isValid: errors.length === 0 && Object.keys(paymentErrors).length === 0,
    errors,
    paymentErrors,
  }
}

export const generateBatchId = (userInfo, existingBatches) => {
  const userFirst = get(userInfo, 'firstName', '')
  const userLast = get(userInfo, 'lastName', '')
  const initials = `${userFirst[0] || ''}${userLast[0] || ''}`.toUpperCase()

  const currentDate = new Date()
  const timestamp = currentDate.getTime()
  const prefix = `${timestamp}-${initials}-`

  // Find all existing batchIds matching initials
  const numbers = existingBatches
    .map(b => b.batchId)
    .filter(id => id && id.split('-')[1] === initials)
    .map(id => {
      const parts = id.split('-')
      return parseInt(parts[2], 10)
    })
    .filter(n => !isNaN(n))

  // Determine next number and pad to 4 digits
  const next = numbers.length ? Math.max(...numbers) + 1 : 1
  const nextStr = String(next).padStart(3, '0')
  return `${prefix}${nextStr}`
}

export const checkDateMatch = (dateRange, row) => {
  let dateMatch = true
  if (dateRange[0] && dateRange[1] && row.createdAt) {
    try {
      const createdDate = typeof row.createdAt === 'string' ? parseISO(row.createdAt) : new Date(row.createdAt)
      // Use start of day for start date and end of day for end date to include the full date range
      const startDate = startOfDay(dateRange[0])
      const endDate = endOfDay(dateRange[1])
      dateMatch = isWithinInterval(createdDate, {
        start: startDate,
        end: endDate,
      })
    } catch (error) {
      console.warn(T.ERROR_PARSING_DATE_FOR_FILTERING, error)
      dateMatch = true // If date parsing fails, include the row
    }
  }
  return dateMatch
}

export const getInvoicesCount = row => {
  let invoices = []

  if (row.payments) {
    invoices = (row.payments || []).flatMap(payment => {
      return payment.selectedInvoices || []
    })
  } else {
    invoices = row.selectedInvoices || []
  }

  return invoices.length
}

export const formatStatusText = (accountsCount, invoicesCount, paymentsCount) => {
  const accountText = accountsCount === 1 ? T.ACCOUNT : T.ACCOUNTS
  const invoiceText = invoicesCount === 1 ? T.INVOICE : T.INVOICES
  const paymentText = paymentsCount === 1 ? T.PAYMENT : T.PAYMENTS

  return `${accountsCount} ${accountText} | ${invoicesCount} ${invoiceText} | ${paymentsCount} ${paymentText}`.toLowerCase()
}
